import { tokens } from '../design-system/tokens';

interface LimitReachedOverlayProps {
  type: 'session' | 'daily';
  extensionAvailable: boolean;
  onExtend: () => void;
  onScheduleDemo: () => void;
  extending: boolean;
  extendError?: string;
}

export function LimitReachedOverlay({
  type,
  extensionAvailable,
  onExtend,
  onScheduleDemo,
  extending,
  extendError,
}: LimitReachedOverlayProps) {
  const title =
    type === 'session'
      ? 'Límite de sesión alcanzado'
      : 'Límite de mensajes alcanzado';

  const subtitle =
    type === 'session'
      ? 'Has alcanzado el número máximo de mensajes para esta sesión. Agendá una demo para conocer todas las funcionalidades.'
      : extensionAvailable
        ? 'Has alcanzado el límite de mensajes para esta demo. Podés extender tu acceso para seguir probando.'
        : 'Has alcanzado el límite de mensajes para esta demo.';

  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>
        {/* Icon */}
        <div style={iconContainerStyle}>
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke={tokens.colors.warning.main}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        {/* Title */}
        <h3 style={titleStyle}>{title}</h3>

        {/* Subtitle */}
        <p style={subtitleStyle}>{subtitle}</p>

        {/* CTA area */}
        <div style={ctaContainerStyle}>
          {type === 'session' && (
            <button
              onClick={onScheduleDemo}
              style={primaryButtonStyle}
            >
              Agendar una Demo
            </button>
          )}

          {type === 'daily' && extensionAvailable && (
            <button
              onClick={onExtend}
              disabled={extending}
              style={{
                ...primaryButtonStyle,
                opacity: extending ? 0.7 : 1,
                cursor: extending ? 'not-allowed' : 'pointer',
              }}
            >
              {extending ? (
                <span style={loadingContentStyle}>
                  <span style={spinnerStyle} />
                  Extendiendo...
                </span>
              ) : (
                'Extender Acceso'
              )}
            </button>
          )}

          {type === 'daily' && !extensionAvailable && (
            <p style={comeBackStyle}>
              Vuelve mañana
            </p>
          )}
        </div>

        {/* Error message */}
        {extendError && (
          <p style={errorStyle}>{extendError}</p>
        )}
      </div>
    </div>
  );
}

// ============================================
// Styles
// ============================================

const overlayStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.75)',
  backdropFilter: 'blur(2px)',
  WebkitBackdropFilter: 'blur(2px)',
  zIndex: 10,
  padding: tokens.spacing[4],
};

const cardStyle: React.CSSProperties = {
  background: tokens.semantic.surface,
  borderRadius: tokens.borderRadius.xl,
  boxShadow: tokens.shadows.lg,
  border: `1px solid ${tokens.semantic.border}`,
  padding: `${tokens.spacing[8]} ${tokens.spacing[6]}`,
  maxWidth: '340px',
  width: '100%',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: tokens.spacing[3],
};

const iconContainerStyle: React.CSSProperties = {
  width: '48px',
  height: '48px',
  borderRadius: tokens.borderRadius.full,
  backgroundColor: tokens.colors.warning.light,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: tokens.spacing[1],
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: tokens.typography.fontSize.lg,
  fontWeight: tokens.typography.fontWeight.semibold,
  color: tokens.semantic.textPrimary,
  lineHeight: tokens.typography.lineHeight.tight,
};

const subtitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: tokens.typography.fontSize.sm,
  fontWeight: tokens.typography.fontWeight.normal,
  color: tokens.semantic.textSecondary,
  lineHeight: tokens.typography.lineHeight.normal,
};

const ctaContainerStyle: React.CSSProperties = {
  marginTop: tokens.spacing[3],
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const primaryButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: `${tokens.spacing[3]} ${tokens.spacing[6]}`,
  fontSize: tokens.typography.fontSize.sm,
  fontWeight: tokens.typography.fontWeight.semibold,
  color: '#ffffff',
  backgroundColor: tokens.semantic.ctaPrimary,
  border: 'none',
  borderRadius: tokens.borderRadius.lg,
  cursor: 'pointer',
  transition: `background-color ${tokens.transitions.fast}`,
  minWidth: '180px',
};

const comeBackStyle: React.CSSProperties = {
  margin: 0,
  fontSize: tokens.typography.fontSize.sm,
  fontWeight: tokens.typography.fontWeight.medium,
  color: tokens.semantic.textMuted,
  fontStyle: 'italic',
};

const errorStyle: React.CSSProperties = {
  margin: 0,
  marginTop: tokens.spacing[2],
  fontSize: tokens.typography.fontSize.xs,
  color: tokens.colors.error.main,
  fontWeight: tokens.typography.fontWeight.medium,
};

const loadingContentStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: tokens.spacing[2],
};

const spinnerStyle: React.CSSProperties = {
  display: 'inline-block',
  width: '14px',
  height: '14px',
  border: '2px solid rgba(255, 255, 255, 0.3)',
  borderTopColor: '#ffffff',
  borderRadius: '50%',
  animation: 'spin 0.6s linear infinite',
};
